/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import type { Accept } from 'react-dropzone';

import { Typography2 } from '@coursera/cds-core';
import { UploadIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/enterprise-ui';

type Props = {
  onDrop: (x0: { files: Array<File> }) => void;
  defaultMessage?: string;
  singleFileOnly?: boolean;
  defaultFileMessage?: string;
  children?: React.ReactNode;
  accept?: Accept;
  showUploadIcon?: boolean;
  zoneCss?: SerializedStyles;
  /**
   * `stacked` centers the icon and message vertically, dropping the "Drop file(s) here or"
   * prefix in favor of just the `defaultMessage`/`defaultFileMessage` pair.
   * @default default
   */
  layout?: 'default' | 'stacked';
};

const styles = {
  stackedZone: css`
    box-sizing: border-box;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-400) 0;
    text-align: center;
  `,
};

export const FileUploadZone = ({
  defaultMessage = _t('browse files'),
  defaultFileMessage = _t('You can upload 1 file at a time'),
  singleFileOnly = false,
  accept,
  children,
  onDrop,
  showUploadIcon = true,
  zoneCss,
  layout = 'default',
}: Props) => {
  const instructions = singleFileOnly ? _t('Drop file here or ') : _t('Drop files here or ');
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files: Array<File>) => {
      onDrop({ files });
    },
    accept,
  });

  if (layout === 'stacked') {
    return (
      <div>
        <div>
          <div css={[styles.stackedZone, zoneCss]} {...getRootProps()}>
            <input {...getInputProps()} data-testid="file-input" />
            {showUploadIcon ? <UploadIcon size="large" /> : null}
            <Typography2 component="p">{defaultMessage}</Typography2>
            {singleFileOnly && (
              <Typography2 component="p" color="supportText" variant="bodySecondary">
                {defaultFileMessage}
              </Typography2>
            )}
          </div>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        <div
          css={css`
            box-sizing: border-box;
            border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--cds-spacing-400) 0;
          `}
          {...getRootProps()}
        >
          <input {...getInputProps()} data-testid="file-input" />
          <UploadIcon size="large" color="interactive" />
          <div
            css={css`
              margin-left: var(--cds-spacing-200);
            `}
          >
            <Typography2 component="span">{instructions}</Typography2>
            <Typography2 component="span" color="emphasis.primary">
              {defaultMessage}
            </Typography2>
            {singleFileOnly && (
              <Typography2 component="p" color="supportText" variant="bodySecondary">
                {defaultFileMessage}
              </Typography2>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
